import React, { useEffect, useMemo, useState } from 'react';
import styles from '../styles/stepProfile.module.css';
import logosImg from '../assets/web_logos_bandera.svg';

// imágenes (según tus carpetas en src/assets)
import amigosImg from '../assets/como_viajas/amigos.jpg';
import familiaImg from '../assets/como_viajas/familia.jpg';
import parejaImg from '../assets/como_viajas/pareja.jpg';
import soloImg from '../assets/como_viajas/solo.jpg';

import deportesImg from '../assets/apetece/deportes.jpg';
import naturalezaImg from '../assets/apetece/naturaleza.jpg';
import patrimonioImg from '../assets/apetece/patrimonio.jpg';
import pueblosImg from '../assets/apetece/pueblos.jpg';

interface Props {
  perfil: string;
  setPerfil: (p: string) => void;
  onNext: () => void;
  onBack: () => void;
}

type Option = {
  id: string;
  label: string;
  img: string;
  description: string;
};

const StepProfile: React.FC<Props> = ({ perfil, setPerfil, onNext, onBack }) => {
  const comoViajasOptions: Option[] = useMemo(
    () => [
      {
        id: 'amigos',
        label: 'Con amigos',
        img: amigosImg,
        description: 'Un viaje con amigos, buscando planes variados y ambiente local',
      },
      {
        id: 'pareja',
        label: 'En pareja',
        img: parejaImg,
        description: 'Una escapada en pareja, con rincones especiales y experiencias tranquilas',
      },
      {
        id: 'familia',
        label: 'Familiar',
        img: familiaImg,
        description: 'Un viaje en familia, equilibrando comodidad y actividades para todos',
      },
      {
        id: 'solo',
        label: 'Solo',
        img: soloImg,
        description: 'Un viaje en solitario, flexible y con espacios para explorar a tu ritmo',
      },
    ],
    [],
  );

  const apeteceOptions: Option[] = useMemo(
    () => [
      {
        id: 'patrimonio',
        label: 'Patrimonio y Cultura',
        img: patrimonioImg,
        description: 'con ganas de patrimonio, cultura y visitas con historia',
      },
      {
        id: 'naturaleza',
        label: 'Naturaleza y ocio',
        img: naturalezaImg,
        description: 'con ganas de naturaleza, miradores y paseos al aire libre',
      },
      {
        id: 'deportes',
        label: 'Actividades',
        img: deportesImg,
        description: 'con ganas de actividad, rutas y planes dinámicos',
      },
      {
        id: 'pueblos',
        label: 'Nuestros pueblos',
        img: pueblosImg,
        description: 'con ganas de pueblos con encanto, plazas y gastronomía local',
      },
    ],
    [],
  );

  const [selectedComoViajas, setSelectedComoViajas] = useState<string | null>(null);
  const [selectedApetece, setSelectedApetece] = useState<string | null>(null);

  const selectedTop = useMemo(
    () => comoViajasOptions.find((o) => o.id === selectedComoViajas) || null,
    [comoViajasOptions, selectedComoViajas],
  );
  const selectedBottom = useMemo(
    () => apeteceOptions.find((o) => o.id === selectedApetece) || null,
    [apeteceOptions, selectedApetece],
  );

  // Cuando hay ambas selecciones, se construye el perfil automáticamente
  useEffect(() => {
    if (!selectedTop || !selectedBottom) return;
    setPerfil(`${selectedTop.description} y ${selectedBottom.description}`);
  }, [selectedTop, selectedBottom, setPerfil]);

  // ✅ Permitir continuar si hay texto manual en el input
  const canContinue = perfil.trim().length > 0 || (!!selectedTop && !!selectedBottom);

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        if (!canContinue) return;
        onNext();
      }}
    >
      <h2 className={styles.title}>
        Cuéntanos sobre ti: ¿qué tipo de experiencias te gustaría vivir en tu viaje?
      </h2>

      {/* Input (manual o autogenerado) */}
      <div className={styles.profileGroup}>
        <div className={styles.field}>
          <input
            type="text"
            value={perfil}
            onChange={(e) => setPerfil(e.target.value)}
            placeholder="Ej: Quiero naturaleza y pueblos con encanto, soy español y tengo 33 años"
            required
          />
        </div>

        {!canContinue && (
          <p className={styles.helper}>
            Escribe tu perfil o elige <strong>cómo viajas</strong> y <strong>qué te apetece</strong>{' '}
            para continuar.
          </p>
        )}
      </div>

      {/* Selector visual */}
      <div className={styles.selectorWrap}>
        <div className={styles.block}>
          <h3 className={styles.blockTitle}>¿Cómo viajas?</h3>
          <div className={styles.choicesRow}>
            {comoViajasOptions.map((opt) => {
              const selected = opt.id === selectedComoViajas;
              return (
                <button
                  key={opt.id}
                  type="button"
                  className={`${styles.choice} ${selected ? styles.choiceSelected : ''}`}
                  onClick={() => setSelectedComoViajas(opt.id)}
                >
                  <span className={styles.choiceImgWrap}>
                    <img className={styles.choiceImg} src={opt.img} alt={opt.label} />
                  </span>
                  <span className={styles.choiceLabel}>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.block}>
          <h3 className={styles.blockTitle}>¿Qué te apetece?</h3>
          <div className={styles.choicesRow}>
            {apeteceOptions.map((opt) => {
              const selected = opt.id === selectedApetece;
              return (
                <button
                  key={opt.id}
                  type="button"
                  className={`${styles.choice} ${selected ? styles.choiceSelected : ''}`}
                  onClick={() => setSelectedApetece(opt.id)}
                >
                  <span className={styles.choiceImgWrap}>
                    <img className={styles.choiceImg} src={opt.img} alt={opt.label} />
                  </span>
                  <span className={styles.choiceLabel}>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className={styles.buttons}>
        <button type="button" onClick={onBack} className={styles.backButton}>
          Atrás
        </button>
        <button type="submit" className={styles.nextButton} disabled={!canContinue}>
          Continuar
        </button>
      </div>

      <div className={styles.footerLogos}>
        <img src={logosImg} alt="NextGenerationEU" />
      </div>
    </form>
  );
};

export default StepProfile;